<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';

import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  hideScreenTransitionOverlay,
  paintThen,
} from 'dashboard/helper/screenTransitionOverlay';
import DealHeader from './DealHeader.vue';
import DealAbout from './DealAbout.vue';
import DealConversation from './DealConversation.vue';
import DealInsights from './DealInsights.vue';
import DealActivities from './DealActivities.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import DealActionsPanel from './DealActionsPanel.vue';
import DealProposals from './DealProposals.vue';
import DealContracts from './DealContracts.vue';
import ConversationProducts from 'dashboard/components/widgets/conversation/products/ConversationProducts.vue';
import ContactRecordings from 'dashboard/components-next/Contacts/ContactRecordings.vue';
import ContactSienge from 'dashboard/components-next/Contacts/ContactSienge.vue';
import ContactMedia from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMedia.vue';
import DealUnit from './DealUnit.vue';
import ProposalsAPI from 'dashboard/api/proposals';
import ContractsAPI from 'dashboard/api/contracts';
import { useDealRecord } from './useDealRecord';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const funnelId = computed(() => route.query.funnelId);

const { conversation, mapping, funnel, stages, isLoading, load } =
  useDealRecord(props.conversationId, funnelId);

// Painel direito em abas (a conversa fica fixa à esquerda). A aba Proposta só
// aparece para contas com template de proposta liberado (hoje: conta 1).
const hasProposalTemplates = ref(false);
// Aba Contrato: só onde há modelo de contrato liberado (hoje: conta 1).
const hasContracts = ref(false);
const contactId = computed(() => conversation.value?.meta?.sender?.id);
const TABS = computed(() => [
  { key: 'acoes', label: t('DEAL_RECORD.TABS.ACTIONS') },
  { key: 'resumo', label: t('DEAL_RECORD.TABS.DEAL') },
  { key: 'contato', label: t('DEAL_RECORD.TABS.CONTACT') },
  ...(contactId.value
    ? [
        {
          key: 'reunioes',
          label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.RECORDINGS'),
        },
        { key: 'midias', label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.MEDIA') },
      ]
    : []),
  { key: 'produtos', label: t('DEAL_RECORD.TABS.PRODUCTS') },
  { key: 'unidade', label: t('DEAL_RECORD.TABS.UNIT') },
  ...(hasProposalTemplates.value
    ? [{ key: 'proposta', label: t('DEAL_RECORD.TABS.PROPOSAL') }]
    : []),
  ...(hasContracts.value
    ? [{ key: 'contrato', label: t('DEALS.CONTRACTS.TAB') }]
    : []),
  { key: 'atividades', label: t('DEAL_RECORD.TABS.ACTIVITIES') },
  { key: 'historico', label: t('DEAL_RECORD.TABS.HISTORY') },
  { key: 'ia', label: t('DEAL_RECORD.TABS.AI') },
]);
const activeTabKey = ref('acoes');
const activeTabIndex = computed(() =>
  Math.max(
    0,
    TABS.value.findIndex(tab => tab.key === activeTabKey.value)
  )
);
const onTabChanged = tab => {
  if (tab?.key) activeTabKey.value = tab.key;
};

const isNotFound = computed(() => !isLoading.value && !conversation.value?.id);

const goToPipeline = () => {
  // Volta para o MESMO funil de onde o negócio foi aberto (preservado em
  // route.query.funnelId pelo handleCardClick). Sem isso o back caía sempre
  // no funil padrão (o primeiro), independentemente de onde o usuário estava.
  const fid = funnelId.value;
  router.push(
    fid
      ? {
          name: 'pipeline_funnel_view',
          params: { accountId: route.params.accountId, funnelId: fid },
        }
      : { name: 'pipeline_view' }
  );
};

// Mudança de funil/etapa vinda do DealActionsPanel → refetch para a barra de
// etapas do header acompanhar. source 'header' já recarrega via prop reload.
const onFunnelUpdated = event => {
  if (event.detail?.source !== 'header') load();
};

onMounted(async () => {
  load();
  // Times para o seletor da aba "Ações" (agentes já vêm via DealConversation).
  store.dispatch('teams/get');
  ProposalsAPI.templates()
    .then(({ data }) => {
      hasProposalTemplates.value = Array.isArray(data) && data.length > 0;
    })
    .catch(() => {
      hasProposalTemplates.value = false;
    });
  ContractsAPI.availability()
    .then(({ data }) => {
      hasContracts.value = data?.available === true;
    })
    .catch(() => {
      hasContracts.value = false;
    });
  window.addEventListener('conversation-funnel-updated', onFunnelUpdated);
  // Derruba o overlay de transição (aberto no clique do card) só DEPOIS da
  // primeira pintura desta tela — o usuário nunca vê tela congelada.
  await paintThen();
  hideScreenTransitionOverlay();
});

onUnmounted(() => {
  window.removeEventListener('conversation-funnel-updated', onFunnelUpdated);
});
</script>

<template>
  <div
    class="flex flex-col flex-1 h-full min-h-0 overflow-hidden bg-n-background"
  >
    <div
      v-if="isLoading"
      class="flex items-center justify-center w-full h-full py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <EmptyStateLayout
      v-else-if="isNotFound"
      :title="t('DEAL_RECORD.NOT_FOUND.TITLE')"
      :subtitle="t('DEAL_RECORD.NOT_FOUND.SUBTITLE')"
    />
    <template v-else>
      <!-- Full-width header + clickable stage bar -->
      <DealHeader
        class="shrink-0 border-b border-n-weak"
        :conversation="conversation"
        :mapping="mapping"
        :funnel="funnel"
        :stages="stages"
        :reload="load"
        @back="goToPipeline"
      />

      <!-- 2 colunas: Conversa (esquerda, ~40%) | Painel de abas (direita, ~60%) -->
      <div
        class="flex flex-col flex-1 min-h-0 overflow-hidden lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
      >
        <DealConversation
          class="min-w-0 min-h-0 border-b border-n-weak lg:border-b-0 lg:border-r lg:overflow-hidden"
          :conversation="conversation"
          :mapping="mapping"
        />

        <div class="flex flex-col min-w-0 min-h-0 lg:overflow-hidden">
          <div
            class="flex items-center px-4 py-3 border-b border-n-weak shrink-0 overflow-x-auto no-scrollbar"
          >
            <TabBar
              compact
              :tabs="TABS"
              :initial-active-tab="activeTabIndex"
              @tab-changed="onTabChanged"
            />
          </div>

          <div class="flex-1 min-h-0 overflow-y-auto">
            <DealAbout
              v-if="activeTabKey === 'resumo'"
              section="resumo"
              :conversation="conversation"
              :mapping="mapping"
              :funnel="funnel"
              @update="load"
            />
            <div v-else-if="activeTabKey === 'contato'">
              <DealAbout
                section="contato"
                :conversation="conversation"
                :mapping="mapping"
                :funnel="funnel"
                @update="load"
              />
              <ContactSienge
                v-if="contactId"
                :key="contactId"
                :contact-id="contactId"
              />
            </div>
            <ContactRecordings
              v-else-if="activeTabKey === 'reunioes' && contactId"
              :key="`recordings-${contactId}`"
              :contact-id="contactId"
            />
            <ContactMedia
              v-else-if="activeTabKey === 'midias' && contactId"
              :key="`media-${contactId}`"
              :contact-id="contactId"
            />
            <div v-else-if="activeTabKey === 'acoes'" class="p-4">
              <DealActionsPanel
                v-if="conversation?.id"
                :conversation="conversation"
              />
            </div>
            <div v-else-if="activeTabKey === 'atividades'" class="p-4">
              <DealActivities
                :conversation="conversation"
                :mapping="mapping"
                :funnel="funnel"
                @update="load"
              />
            </div>
            <div v-else-if="activeTabKey === 'produtos'">
              <ConversationProducts
                v-if="conversation?.id"
                :conversation-id="conversation.id"
              />
            </div>
            <div v-else-if="activeTabKey === 'unidade'" class="p-4">
              <DealUnit :mapping="mapping" />
            </div>
            <DealProposals
              v-else-if="activeTabKey === 'proposta' && conversation?.id"
              :conversation="conversation"
            />
            <DealContracts
              v-else-if="activeTabKey === 'contrato' && conversation?.id"
              :conversation="conversation"
            />
            <div v-else-if="activeTabKey === 'historico'" class="p-4">
              <DealActivities
                hide-composer
                :conversation="conversation"
                :mapping="mapping"
                :funnel="funnel"
                @update="load"
              />
            </div>
            <DealInsights
              v-else-if="activeTabKey === 'ia'"
              section="ia"
              :conversation="conversation"
              :mapping="mapping"
              :funnel="funnel"
              @update="load"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
